import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

private data class TournamentScoreSection(
    val key: String, val title: String, val description: String,
    val slots: List<TournamentSlotDto>, val rows: List<TournamentResultRow>,
)

private fun completeScoreRows(rows: List<TournamentResultRow>, bonus: Boolean): Boolean =
    rows.isNotEmpty() && rows.all { row ->
        row.scores.all { it != null && it in 0..300 } && (!bonus || row.bonusGameNo != null)
    }

@Composable
fun TournamentScoresTab(
    token: String?, tournament: TournamentDto, data: TournamentAdminData,
    canEdit: Boolean, onReload: () -> Unit,
) {
    val rows = buildTournamentRows(tournament.gamesPerPlayer, data.entries.sortedBy { it.playerName.lowercase() }, data.scores)
    val rowsBySlot = rows.groupBy { it.entry.slotId }
    val sections = groupedTournamentSlots(data.slots).map { (day, slots) ->
        TournamentScoreSection("${day.date}|${day.description}", formatDate(day.date), day.description,
            slots, slots.flatMap { rowsBySlot[it.id].orEmpty() })
    }.toMutableList()
    val slotIds = data.slots.map { it.id }.toSet()
    val unassigned = rows.filter { it.entry.slotId !in slotIds }
    if (unassigned.isNotEmpty()) sections.add(TournamentScoreSection("unassigned", "Bez přiřazeného slotu", "", emptyList(), unassigned))
    var dirtySections by remember(tournament.id) { mutableStateOf<Set<String>>(emptySet()) }
    LaunchedEffect(sections.map { it.key }) { dirtySections = dirtySections.intersect(sections.map { it.key }.toSet()) }

    H3 { Text(if (tournament.type == TOURNAMENT_BRACKET) "Kvalifikace" else "Výsledky") }
    if (rows.isEmpty()) {
        P { Text("Nejdřív přidejte účastníky na záložce Účastníci.") }
        return
    }
    if (tournament.type == TOURNAMENT_BRACKET) {
        if (dirtySections.isNotEmpty()) {
            P(attrs = { classes("muted") }) { Text("Před zadáním losu uložte rozpracované dny.") }
        } else TournamentQualificationDraw(token, tournament.id, rows, canEdit, onReload)
    }
    sections.forEach { section ->
        key(tournament.id, section.key) {
            TournamentScoreDay(token, tournament, section, data.scores, data.teamByPlayer, canEdit,
                onDirty = { dirty -> dirtySections = if (dirty) dirtySections + section.key else dirtySections - section.key },
                onReload = onReload)
        }
    }
}

@Composable
private fun TournamentScoreDay(
    token: String?, tournament: TournamentDto, section: TournamentScoreSection,
    allScores: List<TournamentScoreDto>, teamByPlayer: Map<String, String>, canEdit: Boolean,
    onDirty: (Boolean) -> Unit, onReload: () -> Unit,
) {
    val games = tournament.gamesPerPlayer
    val isBonus = tournament.type == TOURNAMENT_BONUS
    val entryIds = section.rows.map { it.entry.id }.toSet()
    val serverScores = allScores.filter { it.entryId in entryIds && it.phase == TOURNAMENT_PHASE_QUALIFICATION }
    var baselineScores by remember { mutableStateOf(serverScores) }
    var baselineEntryIds by remember { mutableStateOf(section.rows.map { it.entry.id }) }
    var baselineSlotIds by remember { mutableStateOf(section.slots.map { it.id }) }
    var grid by remember { mutableStateOf(section.rows.associate { it.entry.id to it.scores.map { score -> score?.toString() ?: "" } }) }
    var bonusGames by remember { mutableStateOf(section.rows.mapNotNull { row -> row.bonusGameNo?.let { row.entry.id to it } }.toMap()) }
    var expanded by remember { mutableStateOf(!completeScoreRows(section.rows, isBonus)) }
    var editing by remember { mutableStateOf(canEdit && !completeScoreRows(section.rows, isBonus)) }
    var dirty by remember { mutableStateOf(false) }
    var saving by remember { mutableStateOf(false) }
    var saved by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    // Reloads caused by another day's save must never replace this day's pending input.
    LaunchedEffect(section.rows, serverScores, games) {
        if (!dirty && !saving) {
            baselineScores = serverScores
            baselineEntryIds = section.rows.map { it.entry.id }
            baselineSlotIds = section.slots.map { it.id }
            grid = section.rows.associate { it.entry.id to it.scores.map { score -> score?.toString() ?: "" } }
            bonusGames = section.rows.mapNotNull { row -> row.bonusGameNo?.let { row.entry.id to it } }.toMap()
        }
    }
    fun changed() { dirty = true; saved = false; error = null; onDirty(true) }
    val draftRows = section.rows.map { row -> row.copy(
        scores = (grid[row.entry.id] ?: List(games) { "" }).map { it.toIntOrNull() }, bonusGameNo = bonusGames[row.entry.id]) }
    val complete = completeScoreRows(draftRows, isBonus)
    val invalidScore = grid.values.flatten().any { it.isNotBlank() && (it.toIntOrNull()?.let { score -> score !in 0..300 } ?: true) }
    val invalidBonus = isBonus && section.rows.any { row ->
        bonusGames[row.entry.id]?.let { grid[row.entry.id]?.getOrNull(it - 1)?.toIntOrNull() == null } == true
    }

    Div(attrs = { classes("tournament-day", "tournament-score-day") }) {
        Div(attrs = { classes("slot-detail-heading") }) {
            Div {
                H4 { Text(section.title) }
                if (section.description.isNotBlank()) P(attrs = { classes("muted") }) { Text(section.description) }
                P(attrs = { classes("muted") }) {
                    Text(when { dirty -> "Neuložené změny"; section.rows.isEmpty() -> "Bez hráčů"; complete -> "Kompletně vyplněno"; else -> "Rozpracováno" } +
                        " · hráčů: ${section.rows.size}")
                }
            }
            Button(attrs = {
                attr("aria-expanded", expanded.toString())
                attr("aria-label", "${if (expanded) "Sbalit" else "Rozbalit"} ${section.title} ${section.description}")
                onClick { expanded = !expanded }
            }) { Text(if (expanded) "Sbalit" else "Rozbalit") }
        }
        if (saved) P { Text("Výsledky této sekce byly uloženy.") }
        error?.let { P(attrs = { classes("editor-error") }) { Text(it) } }
        if (expanded) {
            if (canEdit && !editing && section.rows.isNotEmpty()) Button(attrs = {
                onClick { editing = true; saved = false }
            }) { Text("Upravit výsledky") }
            val scoreTable: @Composable (List<TournamentResultRow>) -> Unit = { tableRows ->
                Div(attrs = { classes("table-scroll") }) {
                    Table(attrs = { classes("public-table") }) {
                        Tr {
                            Th { Text("Hráč") }; Th { Text("Tým / host") }
                            for (i in 1..games) Th { Text("Hra $i") }
                            if (isBonus) Th { Text("Bonifikační") }
                            Th { Text("Součet") }; if (isBonus) Th { Text("S bonusem") }
                        }
                        tableRows.forEach { row -> key(row.entry.id) {
                            val cells = grid[row.entry.id] ?: List(games) { "" }
                            val values = cells.mapNotNull { it.toIntOrNull() }
                            val bonusNo = bonusGames[row.entry.id]
                            Tr {
                                Td { Text(row.entry.playerName) }; Td { Text(teamOrGuestLabel(row.entry.playerId, teamByPlayer)) }
                                cells.forEachIndexed { index, cell -> Td {
                                    if (!editing || !canEdit) Text(cell.ifBlank { "—" }) else Input(type = InputType.Text) {
                                        classes("score-input"); attr("aria-label", "${row.entry.playerName}: hra ${index + 1}")
                                        if (saving) disabled()
                                        value(cell)
                                        onInput {
                                            grid = grid + (row.entry.id to cells.toMutableList().also { list -> list[index] = it.value.filter(Char::isDigit).take(3) })
                                            changed()
                                        }
                                    }
                                } }
                                if (isBonus) Td {
                                    if (!editing || !canEdit) Text(bonusNo?.let { "Hra $it" } ?: "—") else Select(attrs = {
                                        attr("aria-label", "${row.entry.playerName}: bonifikační hra")
                                        if (saving) disabled()
                                        onChange {
                                            val number = it.value?.toIntOrNull()
                                            bonusGames = if (number == null) bonusGames - row.entry.id else bonusGames + (row.entry.id to number)
                                            changed()
                                        }
                                    }) {
                                        Option("", attrs = { if (bonusNo == null) selected() }) { Text("—") }
                                        for (i in 1..games) Option(i.toString(), attrs = { if (bonusNo == i) selected() }) { Text("Hra $i") }
                                    }
                                }
                                Td { Text(if (values.isEmpty()) "—" else values.sum().toString()) }
                                if (isBonus) Td {
                                    val bonusScore = bonusNo?.let { cells.getOrNull(it - 1)?.toIntOrNull() } ?: 0
                                    Text(if (values.isEmpty()) "—" else ((values.sum() * 100 + bonusScore * (tournament.config.bonusPct ?: 50)) / 100.0).toString())
                                }
                            }
                        } }
                    }
                }
            }
            if (section.slots.isEmpty()) scoreTable(section.rows) else section.slots.forEach { slot ->
                Div(attrs = { classes("tournament-slot", "tournament-score-slot") }) {
                    H5 { Text(slotTimeLabel(slot) + (slot.label?.takeIf { it.isNotBlank() }?.let { " · $it" } ?: "")) }
                    val slotRows = section.rows.filter { it.entry.slotId == slot.id }
                    if (slotRows.isEmpty()) P(attrs = { classes("muted") }) { Text("V tomto slotu zatím nejsou žádní hráči.") }
                    else scoreTable(slotRows)
                }
            }
            if (canEdit && editing && section.rows.isNotEmpty()) {
                if (invalidScore) P(attrs = { classes("editor-error") }) { Text("Nához musí být od 0 do 300.") }
                if (invalidBonus) P(attrs = { classes("editor-error") }) { Text("Označená bonifikační hra musí mít vyplněný nához.") }
                Div(attrs = { classes("save-action", "actions") }) {
                    Button(attrs = {
                        attr("aria-label", "Uložit výsledky ${section.title} ${section.description}")
                        if (saving || !dirty || invalidScore || invalidBonus) disabled()
                        onClick {
                            saving = true; error = null
                            val payload = section.rows.flatMap { row -> grid[row.entry.id].orEmpty().mapIndexedNotNull { index, cell ->
                                cell.toIntOrNull()?.let { score -> CreateTournamentScoreRequest(tournament.id, row.entry.id, row.entry.playerId,
                                    score, index + 1, isBonus = isBonus && bonusGames[row.entry.id] == index + 1) }
                            } }
                            scope.launch {
                                try {
                                    baselineScores = Api.saveTournamentDayScores(token, tournament.id, baselineSlotIds,
                                        baselineEntryIds, baselineScores, payload)
                                    dirty = false; onDirty(false); saved = true; editing = false
                                    expanded = !complete
                                    onReload()
                                } catch (e: Throwable) { error = "Uložení sekce selhalo: ${e.apiMessage()}" }
                                saving = false
                            }
                        }
                    }) { Text(if (saving) "Ukládám…" else if (section.slots.isEmpty()) "Uložit sekci" else "Uložit den") }
                    Button(attrs = {
                        if (saving) disabled()
                        onClick {
                            grid = section.rows.associate { it.entry.id to it.scores.map { score -> score?.toString() ?: "" } }
                            bonusGames = section.rows.mapNotNull { row -> row.bonusGameNo?.let { row.entry.id to it } }.toMap()
                            baselineScores = serverScores
                            baselineEntryIds = section.rows.map { it.entry.id }
                            baselineSlotIds = section.slots.map { it.id }
                            dirty = false; onDirty(false); editing = false; error = null
                            onReload()
                        }
                    }) { Text("Zrušit úpravy") }
                }
            }
        }
    }
}
